import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { User, Users, DollarSign, Phone, Plus } from 'lucide-react';
import { useItemContacts, type ImmigrationRole, type ItemContactWithDetails } from '@/hooks/useItemContacts';
import { cn } from '@/lib/utils';

interface RoleSlot {
  role: ImmigrationRole;
  label: string;
  slotIndex?: number;
  required: boolean;
}

interface SlotWithContact extends RoleSlot {
  contact: ItemContactWithDetails | null;
}

interface ItemPeopleEditorProps {
  itemId: string;
  itemName?: string;
  numDependents?: number;
  dealContact?: {
    id: string;
    first_name?: string;
    last_name?: string;
    primary_email?: string;
    primary_phone?: string;
  } | null;
  onClose?: () => void;
  enabledRoles?: string[];
  roleLabels?: Record<string, string>;
  autoOpenAddDialog?: boolean;
  onAddDialogClose?: () => void;
  onEditClick?: () => void;
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  spouse: 'Cônjuge',
  child: 'Filho(a)',
  parent: 'Pai/Mãe',
  sibling: 'Irmão(ã)',
};

/**
 * READ-ONLY view of people associated with an item.
 * For editing, use AddItemModal in edit mode.
 */
export default function ItemPeopleEditor({ 
  itemId, 
  numDependents = 0,
  enabledRoles,
  roleLabels,
  onEditClick,
}: ItemPeopleEditorProps) {
  const { 
    contacts, 
    isLoading, 
  } = useItemContacts(itemId);

  // Generate slots based on enabled roles and numDependents
  const slots = useMemo<RoleSlot[]>(() => {
    const result: RoleSlot[] = [];
    
    // Primary applicant slot (always shown unless explicitly disabled)
    if (!enabledRoles?.length || enabledRoles.includes('primary') || enabledRoles.includes('applicant')) {
      result.push({
        role: 'applicant',
        label: roleLabels?.primary || roleLabels?.applicant || 'Applicant',
        required: true,
      });
    }
    
    // Secondary/Beneficiary slot
    if (enabledRoles?.includes('secondary') || enabledRoles?.includes('beneficiary')) {
      result.push({
        role: 'beneficiary',
        label: roleLabels?.secondary || roleLabels?.beneficiary || 'Secondary Person',
        required: false,
      });
    }
    
    // Petitioner slot
    if (enabledRoles?.includes('petitioner')) {
      result.push({
        role: 'petitioner',
        label: roleLabels?.petitioner || 'Petitioner',
        required: false,
      });
    }
    
    // Dependent slots based on numDependents
    if (numDependents && numDependents > 0) {
      for (let i = 1; i <= numDependents; i++) {
        result.push({
          role: 'dependent',
          label: `${roleLabels?.dependent || 'Family Member'} ${i}`,
          slotIndex: i,
          required: false,
        });
      }
    }
    
    // Payer slot (required for closing)
    result.push({
      role: 'payer',
      label: 'Payer',
      required: true,
    });
    
    // POC slot (not required for closing)
    result.push({
      role: 'poc',
      label: 'Point of Contact',
      required: false,
    });
    
    return result;
  }, [enabledRoles, roleLabels, numDependents]);

  // Map slots with assigned contacts
  const slotsWithContacts = useMemo<SlotWithContact[]>(() => {
    return slots.map((slot) => {
      // For dependents, match by slot index
      if (slot.role === 'dependent' && slot.slotIndex) {
        const dependents = contacts.filter(c => c.immigration_role === 'dependent');
        const assigned = dependents[slot.slotIndex - 1] || null;
        return { ...slot, contact: assigned };
      }
      
      // For Payer, find by is_payer flag
      if (slot.role === 'payer') {
        const payer = contacts.find(c => c.is_payer === true);
        return { ...slot, contact: payer || null };
      }
      
      // For POC, find by is_point_of_contact flag
      if (slot.role === 'poc') {
        const poc = contacts.find(c => c.is_point_of_contact === true);
        return { ...slot, contact: poc || null };
      }
      
      // For other roles, find by role
      const assigned = contacts.find(c => c.immigration_role === slot.role);
      return { ...slot, contact: assigned || null };
    });
  }, [slots, contacts]);

  // Find contacts that don't fit any slot (extra people)
  const extraContacts = useMemo(() => {
    const slotContactIds = slotsWithContacts
      .filter(s => s.contact)
      .map(s => s.contact!.id);
    return contacts.filter(c => !slotContactIds.includes(c.id));
  }, [contacts, slotsWithContacts]);

  // Empty slot component - clickable if onEditClick provided
  const EmptySlot = ({ slot }: { slot: RoleSlot }) => {
    const SlotIcon = slot.role === 'payer' ? DollarSign 
                   : slot.role === 'poc' ? Phone 
                   : User;
    
    return (
      <div 
        className={cn(
          "border-2 border-dashed border-amber-400 bg-amber-50/50 dark:bg-amber-900/20 rounded-lg p-4",
          onEditClick && "cursor-pointer hover:bg-amber-100/80 dark:hover:bg-amber-900/40 transition-colors"
        )}
        onClick={onEditClick}
        role={onEditClick ? "button" : undefined}
        tabIndex={onEditClick ? 0 : undefined}
        onKeyDown={onEditClick ? (e) => e.key === 'Enter' && onEditClick() : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
              <SlotIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">
                Não atribuído
              </p>
              <div className="flex gap-2 mt-1">
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700">
                  {slot.label}
                </Badge>
                {slot.required && (
                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                    Obrigatório p/ fechar
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {onEditClick && (
            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <Plus className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Adicionar</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Filled slot component - READ ONLY
  const FilledSlot = ({ slot, contact }: { slot: RoleSlot; contact: ItemContactWithDetails }) => (
    <div className={cn(
      "border rounded-lg p-4 transition-all bg-card",
      contact.immigration_role === 'applicant' 
        ? "border-primary/50 bg-primary/5" 
        : "border-border"
    )}>
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center text-primary-foreground font-semibold",
          contact.immigration_role === 'applicant' ? "bg-primary" : "bg-muted-foreground"
        )}>
          {contact.contact?.first_name?.[0]?.toUpperCase() || 'U'}
        </div>
        <div>
          <p className="font-medium">
            {contact.contact?.first_name} {contact.contact?.last_name}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <Badge variant="secondary" className="text-xs">
              {slot.label}
            </Badge>
            {contact.relationship_type && (
              <Badge variant="outline" className="text-xs">
                {RELATIONSHIP_LABELS[contact.relationship_type] || contact.relationship_type}
              </Badge>
            )}
            {contact.is_payer && (
              <Badge variant="outline" className="text-xs">
                <DollarSign className="w-3 h-3 mr-1" />
                Payer
              </Badge>
            )}
            {contact.is_point_of_contact && (
              <Badge variant="outline" className="text-xs">
                <Phone className="w-3 h-3 mr-1" />
                POC
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            PEOPLE IN THIS CASE
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Slots List */}
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Carregando...</div>
        ) : (
          <div className="space-y-3">
            {slotsWithContacts.map((slot, index) => (
              <div key={`${slot.role}-${slot.slotIndex || index}`}>
                {slot.contact ? (
                  <FilledSlot slot={slot} contact={slot.contact} />
                ) : (
                  <EmptySlot slot={slot} />
                )}
              </div>
            ))}

            {/* Extra contacts (not in any slot) - READ ONLY */}
            {extraContacts.length > 0 && (
              <>
                <Separator className="my-4" />
                <p className="text-xs text-muted-foreground font-medium">Pessoas Adicionais</p>
                {extraContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="border rounded-lg p-4 bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted-foreground/20 flex items-center justify-center font-semibold text-muted-foreground">
                        {contact.contact?.first_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium">
                          {contact.contact?.first_name} {contact.contact?.last_name}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {contact.is_payer && (
                            <Badge variant="outline" className="text-xs">
                              <DollarSign className="w-3 h-3 mr-1" />
                              Payer
                            </Badge>
                          )}
                          {contact.is_point_of_contact && (
                            <Badge variant="outline" className="text-xs">
                              <Phone className="w-3 h-3 mr-1" />
                              POC
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
